import React, { useState, useEffect, useRef } from 'react';

import { closeObjectivePromptModal, openObjectivePromptModal } from 'actions/objectivePromptModalActions';
import CreateGoalPanel from 'components/MeshList/goals/CreateGoalPanel';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import { ObjectiveTreeCreation } from 'components/ObjectiveTagList/ModalPromptView';
import { TagIcon } from 'components/ObjectiveTagList/TagIcon';
import useForceUpdate from 'components/hooks/useForceUpdate';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Edge, Canvas } from 'reaflow';
import { selectTeamPageGlobalFilters } from 'selectors/globalFilters';
import styled from 'styled-components';
import { FlexContainer } from 'styles/common';
import { checkUserIsGoalAdmin } from 'utils/helpers/rolesAccess';

import {
  OBJECTIVE_LEVEL_TAGS,
  SEARCH_TAG_TYPES,
  TEAM_DURATIONS_OBJECT,
  TEAM_TASKS_VIEW_TYPES,
} from 'constants/ProjectConstants';
import { PanningNavigationIcon } from 'constants/icons';

import { getOrganizationTags, getTimeBoundObjectivesTree } from 'services/admin';
import { getTextFromTextMacros } from 'services/helpers/post';
import { showErrorSnackbar } from 'services/snackbar';
import { getTeamTasksForListView } from 'services/task';

import { Typography4, Typography9 } from 'uiToolkit/Typography';

import TreeViewObjectiveCreationModalView from './components/TreeViewObjectiveCreationModal';
import { getNodesEdgesForTree, insertNewObjective, toggleNodeGoalsView } from './helpers';
import BaseNode from './nodes/BaseNode';

function truncate(maxLength) {
  return (string) => {
    if (string.length > maxLength) {
      return `${string.substring(0, maxLength)}...`;
    }
    return string;
  };
}

const truncateAfter20Length = truncate(20);

function buildGoal(goal) {
  const goalProgress = goal.getGoalProgress(0);
  return {
    id: goal.id,
    displayName: truncateAfter20Length(getTextFromTextMacros(goal.text, goal.macros)),
    percentage: goalProgress ? `${goalProgress}%` : '0%',
    children: [],
    hasChildren: false,
    type: 'GOAL',
  };
}
const getGoals = (tagType) => (tagId) => (filters) =>
  getTeamTasksForListView({
    tagId,
    tagType,
    limit: 20,
    filters1: filters,
    durationKey: TEAM_DURATIONS_OBJECT.lastMonth,
    offset: 0,
    resourceType: TEAM_TASKS_VIEW_TYPES.goalTree,
  })
    .then((res) => res.map(buildGoal))
    .catch((err) => []);

export const buildFiltersForTeam = (parentTagId) => [
  { type: 'TYPE', operatorType: 'IN', data: ['GOAL'] },
  { type: 'TAGS', operatorType: 'IN', data: [parentTagId] },
];

export const buildFiltersForTag = (parentTagId) => [{ type: 'TYPE', operatorType: 'IN', data: ['GOAL'] }];

function buildObjective(tag) {
  return {
    id: tag.id,
    displayName: tag.displayName || '',
    description: tag.description || '',
    children: [],
    percentage: '',
    hasChildren: true,
  };
}

export function getDisplayNameForTeam(isEmpty) {
  return isEmpty ? 'No objectives present' : 'Objectives';
}

export const getDisplayNameForTags = (tag) => (isEmpty) => tag;

export const OBJECTIVE_GOAL_CREATION_MODAL_CHOICES = ['GOAL', 'OBJECTIVE', 'CANCEL'];

export const buildObjectivesData = (getDisplayName) => (activeTags) => {
  const isEmpty = Array.isArray(activeTags) && activeTags.length === 0;
  const objectives = {
    id: 0,
    displayName: getDisplayName(isEmpty),
    description: 'See Objectives',
    children: activeTags,
    hasChildren: !isEmpty,
  };
  return objectives;
};

// for objectives, in every case teamId will be -1 only
export function getObjectives({ parentTagId = null, teamId = -1 } = {}) {
  return getOrganizationTags({
    teamId,
    tagType: parentTagId ? 'SUB_OBJECTIVE_TAG' : 'GOAL', // we are sending goal but it gets objective
    // tagType: 'SUB_OBJECTIVE_TAG', // need to move this outside somewhere
    orgTagId: parentTagId,
  }).then((allTags) => allTags.filter((tag) => tag.active).map(buildObjective));
}

export const loadChildrenData = (...fns) =>
  // [getObjectives({ parentTagId }), getGoals({ filters })]
  Promise.all(fns.map((fn) => fn())).then(mergeObjectivesAndTags);

export const getGoalsForTeam = (teamId) => getGoals(SEARCH_TAG_TYPES.team)(teamId);
export const getGoalsForTags = (tagId) => getGoals(SEARCH_TAG_TYPES.tag)(tagId);

function mergeObjectivesAndTags(res) {
  const res1 = res[0];
  const res2 = res[1];
  return [...res1, ...res2];
}

// function addEmptyNodeIfNoData(data = []) {
//   const result = [...data];
//   if (Array.isArray(result) && result.length === 0) {
//     const node = { displayName: 'No goals present', hasChildren: false };
//     result.push(node);
//   }
//   return result;
// }

// function addDataToTree(processData) {
//   return result => {
//     const newData = addEmptyNodeIfNoData(result);
//     processData(newData);
//   };
// }

// function buildDataAccessor() {
//   return this.setIdAccessor(data => data.id)
//     .setChildrenAccessor(data => data.children)
//     .setBodyDisplayTextAccessor(data => data.displayName)
//     .setTitleDisplayTextAccessor(data => data.percentage);
// }

/*
  parentTagId: if first node to start from is required, then this is needed
  teamId
*/
export default function TreeView({
  isOpenCreateGoalPanel,
  setIsOpenCreateGoalPanel,
  employee,
  selectedTimeBoundPeriod,
  refreshData,
  refreshFn,
  teamId,
  // for objective panel
  isOpenCreateObjectivePanel,
  setIsOpenCreateObjectivePanel,
  selectedCohort = null,
}) {
  const [forceUpdateValue, forceUpdate] = useForceUpdate();
  const [newGoalSubObjective, setNewGoalSubObjective] = useState(undefined);

  const [loading, setLoading] = useState(false);
  const [visibleNodes, setVisibleNodes] = useState([]);
  const [nodesMap, setNodesMap] = useState(null);
  const [visibleEdges, setVisibleEdges] = useState([]);
  const [idTree, setIdTree] = useState(null);
  const [currentTreeIdPath, setCurrentTreeIdPath] = useState(['root']);

  const dispatch = useDispatch();
  const transformWrapperRef = useRef(null);

  const enableObjectiveCreation = checkUserIsGoalAdmin() || employee.isHRISManager;

  const recalculateNodesEdges = ({
    newNodeIdPathArray = currentTreeIdPath,
    newNodesMap = nodesMap,
    newIdTree = idTree,
  }) => {
    if (!newNodesMap || !newIdTree) {
      return;
    }
    const { nodes, edges } = getNodesEdgesForTree({
      nodeIdPathArray: newNodeIdPathArray,
      nodesMap: newNodesMap,
      idTree: newIdTree,
    });
    setVisibleNodes(nodes);
    setVisibleEdges(edges);
    setCurrentTreeIdPath(newNodeIdPathArray);
  };

  const insertObjectiveInTree = ({ objective, nodeIdPathArray }) => {
    insertNewObjective({ newObjective: objective, nodesMap, idTree, nodeIdPathArray });
    recalculateNodesEdges({ newNodeIdPathArray: nodeIdPathArray });
  };

  const updateNodeGoalsMetaData = ({ nodeIdPathArray }) => {
    const currentNodeId = nodeIdPathArray.slice(-1); // gets last item in array
    if (nodesMap[currentNodeId].data.goalsCount === 0) {
      nodesMap[currentNodeId].data.goalsCount = 1;
    }
    nodesMap[currentNodeId].data.isGoalsExpanded = true;
  };
  const globalFilters = useSelector((state) => selectTeamPageGlobalFilters(state), shallowEqual);

  // const redrawTree = () => {
  //   setVisibleEdges(old => [...old]);
  //   setVisibleNodes(old => [...old]);
  // };

  const getObjectiveTreeData = ({ timeBound = selectedTimeBoundPeriod, nodeIdPathArray = currentTreeIdPath } = {}) => {
    if (loading) {
      return;
    }
    setLoading(true);
    getTimeBoundObjectivesTree({ timeBound, globalFilters, teamId, selectedCohort })
      .then(({ nodesMap, idTree }) => {
        setNodesMap(nodesMap);
        setIdTree(idTree);

        recalculateNodesEdges({
          newNodeIdPathArray: nodeIdPathArray,
          newNodesMap: nodesMap,
          newIdTree: idTree,
        });
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const treeIdPath = currentTreeIdPath;

    if (selectedTimeBoundPeriod) {
      getObjectiveTreeData({
        timeBound: selectedTimeBoundPeriod,
        nodeIdPathArray: treeIdPath,
      });
    }
  }, [refreshData, JSON.stringify(globalFilters?.filters)]);

  useEffect(() => {
    if (selectedTimeBoundPeriod) {
      getObjectiveTreeData({
        timeBound: selectedTimeBoundPeriod,
        nodeIdPathArray: ['root'],
      });
    }
  }, [teamId, selectedTimeBoundPeriod]);

  useEffect(() => {
    if (currentTreeIdPath.length <= 2) {
      // eslint-disable-next-line no-unused-expressions
      transformWrapperRef?.current?.centerView();
    }
  }, [currentTreeIdPath.length, teamId, selectedTimeBoundPeriod]);

  const onObjectiveCreationClick = ({ objective, nodeIdPathArray }) => {
    const onCreate = (objective) => {
      insertObjectiveInTree({ objective, nodeIdPathArray });
      dispatch(closeObjectivePromptModal());
    };
    const onCancel = () => {
      dispatch(closeObjectivePromptModal());
    };
    dispatch(
      openObjectivePromptModal({
        contentComponent: (
          <TreeViewObjectiveCreationModalView
            onCancel={onCancel}
            onCreate={onCreate}
            parentObjective={objective}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          />
        ),
        wide: true,
      })
    );
  };

  const onGoalCreationClick = ({ objective, nodeIdPathArray }) => {
    setNewGoalSubObjective(objective);
    setIsOpenCreateGoalPanel(true);
    // optimistically set node to have some goals and expand goals view
    updateNodeGoalsMetaData({ nodeIdPathArray });
  };

  const onGoalObjectiveCreationClick = ({ objective, nodeIdPathArray }) => {
    const promptHandler = (createSelection) => {
      if (createSelection !== OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[2]) {
        if (createSelection === OBJECTIVE_GOAL_CREATION_MODAL_CHOICES[0]) {
          onGoalCreationClick({ objective, nodeIdPathArray });
          dispatch(closeObjectivePromptModal());
        } else {
          onObjectiveCreationClick({ objective, nodeIdPathArray });
        }
      } else {
        dispatch(closeObjectivePromptModal());
      }
    };
    dispatch(
      openObjectivePromptModal({
        contentComponent: <ObjectiveTreeCreation onCompletion={promptHandler} currentObjective={objective} />,
      })
    );
  };

  if (!visibleNodes) {
    return null;
  }

  return (
    <>
      <Container>
        <SvgContainer>
          <TransformWrapper
            wheel={{
              disabled: true,
            }}
            doubleClick={{ disabled: true }}
            options={{
              limitToBounds: false,
            }}
            centerOnInit
            ref={transformWrapperRef}
          >
            <TransformComponent wrapperStyle={{ width: '100%', height: '100%' }}>
              <Canvas
                maxHeight={10000}
                nodes={visibleNodes}
                edges={visibleEdges}
                direction="RIGHT"
                node={(nodeProps) => (
                  <BaseNode
                    nodeProps={nodeProps}
                    recalculateNodesEdges={recalculateNodesEdges}
                    isExpanded={currentTreeIdPath.includes(nodeProps.id)}
                    onGoalsExpandToggle={(nodeId) => {
                      // updates node data in place
                      toggleNodeGoalsView({ nodeId, nodesMap });
                      // so these are needed to trigger re-render and for changes to take effect
                      setVisibleNodes((prevVal) => [...prevVal]);
                      setVisibleEdges((prevVal) => [...prevVal]);
                    }}
                    onObjectiveCreationClick={onObjectiveCreationClick}
                    onGoalObjectiveCreationClick={onGoalObjectiveCreationClick}
                    onGoalCreationClick={onGoalCreationClick}
                    forceUpdateValue={forceUpdateValue}
                    enableObjectiveCreation={enableObjectiveCreation}
                  />
                )}
                style={{ background: '#e5e5e5' }}
                readonly
                arrow={null}
                zoomable
                fit
                edge={<Edge style={{ stroke: '#c4c4c4' }} />}
                // center={false}
              />
            </TransformComponent>
          </TransformWrapper>
        </SvgContainer>
        <InfoOverlayContainer>
          <FlexContainer
            style={{
              background: 'white',
              // background: theme.palette.background.default,
              padding: '5px',
              borderRadius: '4px',
            }}
          >
            <PanningNavigationIcon size="fixed20px" />
            <Typography9 leftGutter={2} display="inline">
              Click and drag to navigate
            </Typography9>
          </FlexContainer>
          <FlexContainer
            style={{
              alignItems: 'center',
              background: 'white',
              // background: theme.palette.background.default,
              padding: '5px',
              borderRadius: '4px',
            }}
          >
            <Typography4 rightGutter={3}>Tags:</Typography4>
            <TagIcon tagId={OBJECTIVE_LEVEL_TAGS[0].value} />
            <Typography4 leftGutter={1.5} rightGutter={3}>
              {OBJECTIVE_LEVEL_TAGS[0].label}
            </Typography4>
            <TagIcon tagId={OBJECTIVE_LEVEL_TAGS[1].value} />
            <Typography4 leftGutter={1.5} rightGutter={3}>
              {OBJECTIVE_LEVEL_TAGS[1].label}
            </Typography4>
            <TagIcon tagId={OBJECTIVE_LEVEL_TAGS[2].value} />
            <Typography4 leftGutter={1.5} rightGutter={3}>
              {OBJECTIVE_LEVEL_TAGS[2].label}
            </Typography4>
            <TagIcon tagId={OBJECTIVE_LEVEL_TAGS[3].value} />
            <Typography4 leftGutter={1.5}>{OBJECTIVE_LEVEL_TAGS[3].label}</Typography4>
          </FlexContainer>
        </InfoOverlayContainer>
      </Container>
      <ObjectiveSidePanel
        open={isOpenCreateObjectivePanel}
        onCancel={() => {
          setIsOpenCreateObjectivePanel(false);
        }}
        selectedTimeBoundPeriod={selectedTimeBoundPeriod}
        addNewObjective={refreshFn}
        isAlignmentAtParentLevel
        setIsOpenCreateGoalPanel={(value) => {
          setIsOpenCreateGoalPanel(value);
        }}
        source="Team Page"
      />
      <CreateGoalPanel
        open={isOpenCreateGoalPanel}
        onCancel={() => {
          setIsOpenCreateGoalPanel(false);
          setNewGoalSubObjective(undefined);
        }}
        employee={employee}
        afterGoalCreation={forceUpdate}
        // afterGoalCreation={forceUpdate}
        subObjective={newGoalSubObjective}
        eventSource="Tree view"
      />
    </>
  );
}

const Container = styled.div`
  position: relative;
  background: ${(props) => props.theme.palette.background.paper};
  ${'' /* padding: 1rem; */}
  margin-bottom: 1rem;
`;

const InfoOverlayContainer = styled.div`
  position: absolute;
  width: 100%;
  top: 32px;
  padding: 0 32px;
  display: flex;
  justify-content: space-between;
  ${'' /* padding: 0.25rem 1rem 0.25rem 0.75rem; */}
  align-items: center;
`;

const SvgContainer = styled.div`
  position: relative;
  width: 100%;
  height: 80vh;
  ${'' /* background: ${props => props.theme.palette.background.default}; */}
  background: white;
  border: 1px solid ${(props) => props.theme.palette.borders.grey};
  border-radius: 4px;
`;

const StyledCanvas = styled(Canvas)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;
